import { androidPerformanceClass } from '../telegram';

/**
 * Quality tiers. Everything that costs fill rate or per-fragment work on weak
 * GPUs is decided here once, at boot; at runtime only DPR and effect density
 * adapt, because neither recompiles a shader.
 */
export type Tier = 'low' | 'medium' | 'high';

export interface Quality {
  tier: Tier;
  /** Resolution bounds for the adaptive DPR controller. */
  maxDpr: number; minDpr: number;
  shadowSize: number;
  /** MSAA only at low pixel ratios, and only where the GPU can afford it. */
  msaa: boolean;
  bloomLevels: number; bloomScale: number;
  /** Pooled impact lights; each one is paid by every lit fragment, even at zero intensity (profiled: ~6% of the frame each). */
  flashLights: number;
  /** Particle ring capacity and the share of optional effect particles kept. */
  particles: number; density: number;
  /** Ambient dust motes around the gate. */
  dust: number;
  /** Fewer, curl-free spawn fog layers. */
  fogLite: boolean;
  /** Segment-count scale for enemy models (the horde is the scene's main triangle cost; profiled: 1 → 0.7
   * halves the horde's GPU cost with no visible change at the gameplay camera). */
  enemyDetail: number;
  /** Render cap (0 = display rate, so 120 Hz+ screens stay smooth). Phones below high stay cool at 60. */
  maxFps: number;
  /** Drawing-buffer pixel budget: the DPR is lowered on big, dense screens to stay under it. */
  maxPixels: number;
}

export const PRESETS: Record<Tier, Omit<Quality, 'maxFps' | 'maxPixels'>> = {
  high: { tier: 'high', maxDpr: 2, minDpr: 1, shadowSize: 2048, msaa: true, bloomLevels: 5, bloomScale: 0.4, flashLights: 1, particles: 12288, density: 1, dust: 360, fogLite: false, enemyDetail: 0.7 },
  medium: { tier: 'medium', maxDpr: 1.5, minDpr: 0.85, shadowSize: 1024, msaa: false, bloomLevels: 4, bloomScale: 0.4, flashLights: 1, particles: 8192, density: 0.75, dust: 240, fogLite: false, enemyDetail: 0.6 },
  low: { tier: 'low', maxDpr: 1.25, minDpr: 0.7, shadowSize: 1024, msaa: false, bloomLevels: 4, bloomScale: 0.4, flashLights: 1, particles: 6144, density: 0.5, dust: 120, fogLite: true, enemyDetail: 0.5 },
};

/** ~2560×1440 on high; phones and weak GPUs much less. */
export const PIXELS: Record<Tier, number> = { high: 3.7e6, medium: 2.3e6, low: 1.4e6 };

export interface DeviceInfo {
  /** Touch-first device (phone/tablet). */
  coarse: boolean;
  /** navigator.deviceMemory in GB, when exposed. */
  memory?: number;
  cores?: number;
  /** Unmasked GL renderer string, when exposed. */
  gpu?: string;
  /** Shorter CSS side of the screen. */
  minSide: number;
  /** The device class Telegram's Android client measures and reports (Mini App only). */
  telegramClass?: 'low' | 'average' | 'high' | null;
}

/** Old or entry-level mobile GPUs and software rasterisers. */
const WEAK_GPU = /mali-(?:t\d+|g[35]\d\b|4\d\d)|adreno\D*(?:[1-5]\d\d|6[0-1]\d)\b|powervr|sgx|videocore|swiftshader|llvmpipe|software/i;
/** Integrated desktop graphics: fine at moderate resolution, not at 2x with MSAA. */
const MODEST_GPU = /intel.*(?:hd|uhd) graphics/i;

export function pickTier(d: DeviceInfo): Tier {
  if (d.gpu && WEAK_GPU.test(d.gpu)) return 'low';
  // Telegram knows the Android device better than the GPU string does
  if (d.telegramClass === 'low') return 'low';
  if (d.coarse) {
    if ((d.memory !== undefined && d.memory <= 3) || (d.cores !== undefined && d.cores <= 4)) return 'low';
    // phones are fill-rate bound at native DPR; tablets have the same GPU behind more pixels
    return 'medium';
  }
  if ((d.gpu && MODEST_GPU.test(d.gpu)) || (d.memory !== undefined && d.memory <= 4) || (d.cores !== undefined && d.cores <= 2)) return 'medium';
  return 'high';
}

export function resolveQuality(d: DeviceInfo, override?: string | null): Quality {
  const tier = override === 'low' || override === 'medium' || override === 'high' ? override : pickTier(d);
  return { ...PRESETS[tier], maxFps: d.coarse && tier !== 'high' ? 60 : 0, maxPixels: PIXELS[tier] };
}

/** Read the device; the GL context provides the unmasked renderer where allowed. */
export function detectQuality(gl: WebGLRenderingContext | WebGL2RenderingContext | null, override?: string | null): Quality {
  const nav = navigator as Navigator & { deviceMemory?: number };
  let gpu: string | undefined;
  if (gl) {
    const ext = gl.getExtension('WEBGL_debug_renderer_info');
    gpu = String(ext ? gl.getParameter(ext.UNMASKED_RENDERER_WEBGL) : gl.getParameter(gl.RENDERER));
  }
  return resolveQuality({
    coarse: matchMedia('(pointer: coarse)').matches,
    memory: nav.deviceMemory, cores: nav.hardwareConcurrency, gpu,
    minSide: Math.min(screen.width, screen.height),
    telegramClass: androidPerformanceClass(navigator.userAgent),
  }, override);
}
